import { Link } from 'react-router-dom'
import PolicyPage, { PolicySection, PolicyList } from 'components/compliance/policy-page'
import compliance from 'data/compliance.json'

const { organisation, admissions } = compliance

const principles = [
  'We act honestly, professionally and in the legitimate best interests of each student.',
  'We give information that is accurate as far as we know, and we correct it when we learn that it is out of date or wrong.',
  'We explain entry requirements, costs, conditions and the risks of an application in plain language.',
  'We never guarantee admission, a scholarship or a visa, and we avoid wording that suggests we can.',
  'We recommend programmes on the basis of your circumstances and a reasonable expectation of eligibility.',
  'We tell you when we have a commercial relationship with an institution we are recommending.',
  'We protect your information and share it only for purposes you would expect, as set out in our privacy policy.',
  'We treat every student fairly and respectfully, and without unlawful discrimination.',
  'We take particular care with applications from minors and from applicants in vulnerable situations.',
  'We ask you for complete and genuine documents, and we refuse to submit documents that are false, altered or deliberately misleading.'
]

const responsibilities = [
  'Give us truthful, complete and current information and documents.',
  'Check your application forms and confirm that they are accurate before they are submitted.',
  'Meet the deadlines set by institutions and government authorities.',
  'Pay legitimate third party charges directly through the authorised channel.',
  'Tell us promptly if your circumstances change.',
  'Follow the rules of the institution, the immigration authority and the country you travel to.'
]

function StudentCommitment() {
  return (
    <PolicyPage
      title='Student Commitment and Counselling Charter'
      description='How UHDSS works with students: acting in your best interests, giving accurate information, assessing suitability, and never guaranteeing admission or visa outcomes.'
      intro='This charter explains what you can expect from us, how we decide what to recommend, and what we ask of you in return.'
    >
      <PolicySection title='Our commitment to you'>
        <p className='dark-blue fs-6'>
          {organisation.legalName}, referred to here as UHDSS, provides international education
          counselling. We are committed to working ethically and transparently, and to putting the
          legitimate best interests of each student first, so that you can make an informed
          decision about your education and your future.
        </p>
      </PolicySection>

      <PolicySection title='How we work'>
        <PolicyList items={principles} />
      </PolicySection>

      <PolicySection title='Checking whether an application is suitable'>
        <p className='dark-blue fs-6'>
          Before we recommend an institution or a programme, we try to understand your situation.
          Depending on the destination and the course, we look at:
        </p>
        <PolicyList items={admissions.suitabilityFactors} />
        <p className='dark-blue fs-6'>
          Our aim is not to submit as many applications as possible. It is to help you find
          opportunities for which you have a reasonable basis of eligibility. Where we think an
          application is unlikely to succeed, we will say so and explain why.
        </p>
        <p className='dark-blue fs-6'>
          This assessment is guidance only. The formal decision is always made by the university or
          college, and by the immigration authority for anything related to a visa. See our{' '}
          <Link className='text-decoration-none' to='/admissions-disclaimer'>admissions, scholarship and visa disclaimer</Link>.
        </p>
      </PolicySection>

      <PolicySection title='Your choice'>
        <p className='dark-blue fs-6'>
          You make the final decision about your institution, your programme and your destination.
          Where we can, we will give you enough information to compare suitable options. You are
          free to accept or reject our recommendations, ask for alternatives, apply on your own,
          take independent advice, or stop working with us at any time.
        </p>
      </PolicySection>

      <PolicySection title='What we ask of you'>
        <PolicyList items={responsibilities} />
      </PolicySection>

      <PolicySection title='Costs and payments'>
        <p className='dark-blue fs-6'>
          We explain the costs involved before you commit to anything. Our{' '}
          <Link className='text-decoration-none' to='/fees'>fees and service charges policy</Link>{' '}
          sets out what we provide at no charge and how any charge would be told to you, and our{' '}
          <Link className='text-decoration-none' to='/payments'>tuition and student payments policy</Link>{' '}
          explains how tuition, deposits and government fees should be paid.
        </p>
      </PolicySection>

      <PolicySection title='Professional standards'>
        <p className='dark-blue fs-6'>
          We keep the knowledge of our counsellors up to date, and we record the training our staff
          complete. You can read about this in our{' '}
          <Link className='text-decoration-none' to='/professional-standards'>professional standards and staff development</Link>{' '}
          page.
        </p>
      </PolicySection>

      <PolicySection title='If something goes wrong'>
        <p className='dark-blue fs-6'>
          If you are unhappy with anything we have done, please tell us. Our{' '}
          <Link className='text-decoration-none' to='/complaints'>complaints policy</Link>{' '}
          explains how to raise a concern, how long we aim to take, and what you can do if you are
          not satisfied with our answer. Raising a genuine complaint will never affect how we treat
          you.
        </p>
      </PolicySection>
    </PolicyPage>
  )
}

export default StudentCommitment
